import React, { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform, useSpring, useMotionValue } from 'framer-motion';
import { ArrowDownIcon } from 'lucide-react';
import { useLanguage } from '../lib/i18n';
function FloatingGrid() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let animationId: number;
    let time = 0;
    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resize();
    window.addEventListener('resize', resize);
    const draw = () => {
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      ctx.clearRect(0, 0, w, h);
      const spacing = 40;
      const cols = Math.ceil(w / spacing) + 1;
      const rows = Math.ceil(h / spacing) + 1;
      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          const x = i * spacing;
          const y = j * spacing;
          const cx = w / 2;
          const cy = h / 2;
          const dist = Math.sqrt((x - cx) ** 2 + (y - cy) ** 2);
          const maxDist = Math.sqrt(cx ** 2 + cy ** 2);
          const fade = Math.max(0, 1 - dist / maxDist);
          const wave = Math.sin(time * 0.8 + i * 0.3 + j * 0.2) * 0.5 + 0.5;
          const size = 1 + wave * 1.5;
          const alpha = fade * (0.06 + wave * 0.1);
          ctx.beginPath();
          ctx.arc(x, y, size, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(250, 250, 250, ${alpha})`;
          ctx.fill();
        }
      }
      ctx.strokeStyle = 'rgba(250, 250, 250, 0.025)';
      ctx.lineWidth = 0.5;
      for (let i = 0; i < cols; i += 3) {
        for (let j = 0; j < rows; j += 3) {
          const x1 = i * spacing;
          const y1 = j * spacing;
          const wave1 = Math.sin(time * 0.8 + i * 0.3 + j * 0.2) * 0.5 + 0.5;
          if (wave1 > 0.6 && i + 3 < cols) {
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo((i + 3) * spacing, y1);
            ctx.stroke();
          }
          if (wave1 > 0.7 && j + 3 < rows) {
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x1, (j + 3) * spacing);
            ctx.stroke();
          }
        }
      }
      time += 0.016;
      animationId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, []);
  return <motion.canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 2,
    delay: 0.5
  }} data-id="element-148" />;
}
// Interactive gradient orb that follows mouse with spring physics
function GradientOrb() {
  const mouseX = useMotionValue(typeof window !== 'undefined' ? window.innerWidth / 2 : 500);
  const mouseY = useMotionValue(typeof window !== 'undefined' ? window.innerHeight / 2 : 400);
  const springX = useSpring(mouseX, {
    stiffness: 30,
    damping: 20
  });
  const springY = useSpring(mouseY, {
    stiffness: 30,
    damping: 20
  });
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mouseX.set(e.clientX);
      mouseY.set(e.clientY);
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [mouseX, mouseY]);
  return <motion.div className="fixed pointer-events-none z-0 hidden md:block" style={{
    x: springX,
    y: springY,
    translateX: '-50%',
    translateY: '-50%'
  }} initial={{
    opacity: 0,
    scale: 0.5
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 1.5,
    delay: 1
  }} data-id="element-149">
      <div className="w-[500px] h-[500px] rounded-full" style={{
      background: 'radial-gradient(circle, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 40%, transparent 70%)',
      filter: 'blur(40px)'
    }} data-id="element-150" />
    </motion.div>;
}
// Animated counter
function Counter({
  target,
  suffix = '',
  delay = 0
}: {
  target: number;
  suffix?: string;
  delay?: number;
}) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setStarted(true), delay * 1000);
    return () => clearTimeout(timer);
  }, [delay]);
  useEffect(() => {
    if (!started) return;
    let current = 0;
    const duration = 2000;
    const step = target / (duration / 16);
    const interval = setInterval(() => {
      current += step;
      if (current >= target) {
        setCount(target);
        clearInterval(interval);
      } else {
        setCount(Math.floor(current));
      }
    }, 16);
    return () => clearInterval(interval);
  }, [started, target]);
  return <span data-id="element-151">
      {count}
      {suffix}
    </span>;
}
export function Hero() {
  const {
    scrollY
  } = useScroll();
  const opacity = useTransform(scrollY, [0, 500], [1, 0]);
  const y = useTransform(scrollY, [0, 500], [0, 100]);
  const {
    t
  } = useLanguage();
  const scrollToProjects = () => {
    document.getElementById('projects')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section className="relative min-h-[80vh] md:min-h-screen flex flex-col justify-end px-6 md:px-12 lg:px-20 pb-12 md:pb-32 overflow-hidden" data-id="element-152">
      {/* Animated Dot Grid */}
      <FloatingGrid data-id="element-153" />

      {/* Mouse-following gradient orb */}
      <GradientOrb data-id="element-154" />

      {/* Geometric Accent — top right — REFINED */}
      <div className="absolute top-1/2 right-12 md:right-20 lg:right-32 -translate-y-1/2 pointer-events-none hidden lg:block" data-id="element-155">
        <div className="relative w-72 h-72 xl:w-96 xl:h-96" data-id="element-156">
          {/* Ambient glow — breathes slowly */}
          <motion.div className="absolute inset-0 rounded-full" initial={{
          opacity: 0
        }} animate={{
          opacity: [0, 0.8, 0.5, 0.8, 0]
        }} transition={{
          duration: 8,
          delay: 1,
          repeat: Infinity,
          repeatDelay: 0,
          ease: 'easeInOut'
        }} style={{
          background: 'radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 55%)',
          filter: 'blur(25px)',
          transform: 'scale(1.4)'
        }} data-id="element-157" />

          {/* Static rings — clean, elegant */}
          <motion.svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full" data-id="element-158">
            {/* Outer ring */}
            <motion.circle cx="100" cy="100" r="92" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.4" strokeDasharray="578" initial={{
            strokeDashoffset: 578
          }} animate={{
            strokeDashoffset: 0
          }} transition={{
            duration: 2.5,
            delay: 0.8,
            ease: [0.16, 1, 0.3, 1]
          }} data-id="element-159" />
            {/* Middle ring */}
            <motion.circle cx="100" cy="100" r="60" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="0.3" strokeDasharray="377" initial={{
            strokeDashoffset: 377
          }} animate={{
            strokeDashoffset: 0
          }} transition={{
            duration: 2,
            delay: 1.2,
            ease: [0.16, 1, 0.3, 1]
          }} data-id="element-160" />
            {/* Inner ring */}
            <motion.circle cx="100" cy="100" r="30" fill="none" stroke="rgba(255,255,255,0.09)" strokeWidth="0.3" strokeDasharray="188" initial={{
            strokeDashoffset: 188
          }} animate={{
            strokeDashoffset: 0
          }} transition={{
            duration: 1.6,
            delay: 1.6,
            ease: [0.16, 1, 0.3, 1]
          }} data-id="element-161" />
          </motion.svg>

          {/* Sweeping arc — outer orbit trail */}
          <motion.svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full" style={{
          filter: 'blur(0.3px)'
        }} animate={{
          rotate: 360
        }} transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-162">
            <defs data-id="element-163">
              <linearGradient id="arcTrail1" gradientUnits="userSpaceOnUse" x1="8" y1="100" x2="100" y2="8" data-id="element-164">
                <stop offset="0%" stopColor="rgba(255,255,255,0)" data-id="element-165" />
                <stop offset="60%" stopColor="rgba(255,255,255,0.12)" data-id="element-166" />
                <stop offset="100%" stopColor="rgba(255,255,255,0.35)" data-id="element-167" />
              </linearGradient>
            </defs>
            {/* Arc trail — quarter circle */}
            <circle cx="100" cy="100" r="92" fill="none" stroke="url(#arcTrail1)" strokeWidth="1.2" strokeDasharray="145 433" strokeLinecap="round" data-id="element-168" />
            {/* Leading dot */}
            <circle cx="100" cy="8" r="2.5" fill="rgba(255,255,255,0.6)" data-id="element-169">
              <animate attributeName="r" values="2;3;2" dur="2s" repeatCount="indefinite" data-id="element-170" />
            </circle>
          </motion.svg>

          {/* Sweeping arc — middle orbit, opposite direction */}
          <motion.svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full" style={{
          filter: 'blur(0.2px)'
        }} animate={{
          rotate: -360
        }} transition={{
          duration: 14,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-171">
            <defs data-id="element-172">
              <linearGradient id="arcTrail2" gradientUnits="userSpaceOnUse" x1="160" y1="100" x2="100" y2="160" data-id="element-173">
                <stop offset="0%" stopColor="rgba(255,255,255,0)" data-id="element-174" />
                <stop offset="70%" stopColor="rgba(255,255,255,0.08)" data-id="element-175" />
                <stop offset="100%" stopColor="rgba(255,255,255,0.25)" data-id="element-176" />
              </linearGradient>
            </defs>
            <circle cx="100" cy="100" r="60" fill="none" stroke="url(#arcTrail2)" strokeWidth="0.8" strokeDasharray="95 282" strokeLinecap="round" data-id="element-177" />
            {/* Leading dot */}
            <circle cx="160" cy="100" r="1.8" fill="rgba(255,255,255,0.45)" data-id="element-178">
              <animate attributeName="r" values="1.5;2.2;1.5" dur="3s" repeatCount="indefinite" data-id="element-179" />
            </circle>
          </motion.svg>

          {/* Sweeping arc — inner orbit */}
          <motion.svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full" animate={{
          rotate: 360
        }} transition={{
          duration: 22,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-180">
            <defs data-id="element-181">
              <linearGradient id="arcTrail3" gradientUnits="userSpaceOnUse" x1="100" y1="130" x2="70" y2="100" data-id="element-182">
                <stop offset="0%" stopColor="rgba(255,255,255,0)" data-id="element-183" />
                <stop offset="80%" stopColor="rgba(255,255,255,0.06)" data-id="element-184" />
                <stop offset="100%" stopColor="rgba(255,255,255,0.18)" data-id="element-185" />
              </linearGradient>
            </defs>
            <circle cx="100" cy="100" r="30" fill="none" stroke="url(#arcTrail3)" strokeWidth="0.6" strokeDasharray="47 141" strokeLinecap="round" data-id="element-186" />
            {/* Leading dot */}
            <circle cx="100" cy="130" r="1.2" fill="rgba(255,255,255,0.3)" data-id="element-187">
              <animate attributeName="r" values="1;1.6;1" dur="4s" repeatCount="indefinite" data-id="element-188" />
            </circle>
          </motion.svg>

          {/* Center core */}
          <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          duration: 1.5,
          delay: 2,
          ease: [0.16, 1, 0.3, 1]
        }} data-id="element-189">
            {/* Core glow */}
            <div className="w-4 h-4 rounded-full" style={{
            background: 'radial-gradient(circle, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.15) 50%, transparent 100%)',
            boxShadow: '0 0 20px 6px rgba(255,255,255,0.08), 0 0 40px 12px rgba(255,255,255,0.03)'
          }} data-id="element-190" />
          </motion.div>

          {/* Single breathing pulse from center */}
          <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{
          border: '1px solid rgba(255,255,255,0.08)'
        }} animate={{
          width: [0, 60, 80],
          height: [0, 60, 80],
          opacity: [0, 0.3, 0]
        }} transition={{
          duration: 4,
          delay: 2.5,
          repeat: Infinity,
          ease: 'easeOut'
        }} data-id="element-191" />
        </div>
      </div>

      <motion.div style={{
      opacity,
      y
    }} className="max-w-5xl relative z-10" data-id="element-192">
        {/* Overline */}
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1,
        delay: 0.2
      }} className="text-secondary text-sm tracking-[0.2em] uppercase mb-8 font-light" data-id="element-193">
          {t.heroOverline} — {new Date().getFullYear()}
        </motion.p>

        {/* Main Title */}
        <div className="overflow-hidden mb-2 md:mb-4" data-id="element-194">
          <motion.h1 initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} transition={{
          duration: 1,
          delay: 2.8,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-4xl md:text-7xl lg:text-8xl font-display font-bold text-primary leading-[1.1] tracking-tight animated-gradient-text" data-id="element-195">
            {t.heroTitle1}
          </motion.h1>
        </div>
        <div className="overflow-hidden mb-6 md:mb-10" data-id="element-196">
          <motion.h1 initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} transition={{
          duration: 1,
          delay: 3.0,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-4xl md:text-7xl lg:text-8xl font-display font-bold leading-[1.1] tracking-tight animated-gradient-text" data-id="element-197">
            {t.heroTitle2}
          </motion.h1>
        </div>

        {/* Subtitle */}
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 3.4
      }} className="text-secondary text-base md:text-xl max-w-xl leading-relaxed mb-8 md:mb-12 font-light" data-id="element-198">
          {t.heroSubtitle}
        </motion.p>

        {/* Stats Strip */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 3.6
      }} className="flex gap-8 md:gap-16 mb-10 md:mb-14" data-id="element-199">
          {[{
          value: 15,
          suffix: '+',
          label: t.heroStat1Label
        }, {
          value: 6,
          suffix: '+',
          label: t.heroStat2Label
        }, {
          value: 8,
          suffix: '',
          label: t.heroStat3Label
        }].map((stat, i) => <div key={stat.label} className="relative" data-id="element-200">
              <div className="text-3xl md:text-4xl font-display font-bold text-primary tracking-tight leading-none mb-1" data-id="element-201">
                <Counter target={stat.value} suffix={stat.suffix} delay={3.8 + i * 0.2} data-id="element-202" />
              </div>
              <div className="text-[11px] text-secondary/60 tracking-[0.15em] uppercase font-light" data-id="element-203">
                {stat.label}
              </div>
              {i < 2 && <div className="absolute right-[-20px] md:right-[-32px] top-1 bottom-1 w-px bg-border hidden md:block" data-id="element-204" />}
            </div>)}
        </motion.div>

        {/* Scroll Indicator */}
        <motion.button initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        delay: 4.2
      }} onClick={scrollToProjects} className="group flex items-center gap-4 text-secondary hover:text-primary transition-colors duration-500" data-id="element-205">
          <span className="text-xs tracking-[0.2em] uppercase font-light" data-id="element-206">
            {t.heroScroll}
          </span>
          <div className="w-8 h-8 rounded-full border border-border flex items-center justify-center group-hover:border-secondary transition-colors duration-500" data-id="element-207">
            <ArrowDownIcon className="w-3.5 h-3.5" data-id="element-208" />
          </div>
        </motion.button>
      </motion.div>

      {/* Bottom Line */}
      <motion.div initial={{
      scaleX: 0
    }} animate={{
      scaleX: 1
    }} transition={{
      duration: 1.5,
      delay: 1,
      ease: [0.16, 1, 0.3, 1]
    }} className="absolute bottom-0 left-6 right-6 md:left-12 md:right-12 lg:left-20 lg:right-20 h-px bg-border origin-left" data-id="element-209" />
    </section>;
}